/**
 * Last-valid-geometry retention for step-to-step spotlight transitions.
 *
 * The defect it fixes: when a step advances, the NEW step's targets have no
 * measurements yet (the ids changed; the bounds map still holds the old
 * step's entries and is then reset), so the computed cutouts are empty and
 * the backdrop unmounted for one frame up to ~one poll interval — visibly
 * clearing the dim/blur (most noticeable over the bright rack at the bottom
 * of the screen).
 *
 * The rule (deliberately narrow — the approved checkpoint lifecycle is
 * otherwise unchanged):
 *  - While a step that HAS declared targets is still measuring, keep
 *    rendering the PREVIOUS step's complete geometry snapshot, whole and
 *    untouched (never mixed with partial new measurements).
 *  - The moment the new step's geometry is ready, replace the snapshot
 *    atomically in one render.
 *  - Steps intentionally designed with no targets (watch phases) keep their
 *    approved no-overlay presentation AND clear the snapshot, so retention
 *    can never bleed into them or out of them.
 *  - Retention is tied purely to target readiness — no timers.
 */
import type { SpotRect } from './spotlightGeometry';

export interface SpotlightGeometry {
  cutouts: SpotRect[];
  passthroughs: SpotRect[];
  fullDim: boolean;
}

export interface ResolvedGeometry {
  /** What to render this frame (null ⇒ no overlay — approved no-target state). */
  geometry: SpotlightGeometry | null;
  /** True when showing the held previous snapshot (render it inert). */
  retained: boolean;
  /** Snapshot to carry to the next render. */
  nextLast: SpotlightGeometry | null;
}

export function resolveDisplayedGeometry(args: {
  last: SpotlightGeometry | null;
  /** Does the CURRENT step declare any spotlight presentation? */
  stepHasTargets: boolean;
  fullDim: boolean;
  cutouts: SpotRect[];
  passthroughs: SpotRect[];
}): ResolvedGeometry {
  const { last, stepHasTargets, fullDim, cutouts, passthroughs } = args;

  // Intentional no-target step (or no step): approved unmount, snapshot
  // dropped so it can never resurface later.
  if (!stepHasTargets) {
    return { geometry: null, retained: false, nextLast: null };
  }

  const ready = fullDim || cutouts.length > 0;
  if (ready) {
    const geometry: SpotlightGeometry = { cutouts, passthroughs, fullDim };
    return { geometry, retained: false, nextLast: geometry };
  }

  // Measuring: hold the previous complete snapshot, exactly as it was.
  return { geometry: last, retained: last !== null, nextLast: last };
}
